import axios from "axios";

import Cookies from 'universal-cookie';

import { notifyAuthExpired } from "./authEvents";



const cookie = new Cookies();



// Base API URL (default to local dev if env not provided)
export const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8082"

// Marks a request as one that carried a real bearer token, so the 401
// interceptor can tell "your session died" apart from "these credentials are
// wrong" or "S3 rejected the upload".
const AUTH_REQUEST_FLAG = "__essAuthenticatedRequest";

// The token endpoint answers bad credentials with 401. It must never be read as
// an expiry, or a typo'd password would clear cookies and toast "session ended"
// over the Login form's own inline error.
const AUTH_ENDPOINTS = ["api/token/", "api/token/refresh/"];

const isAuthEndpoint = (url = "") =>
    AUTH_ENDPOINTS.some((endpoint) => String(url).includes(endpoint));


export const performRequest = (
    method,
    url,
    params,
    login = true,
    type = "json",
) => {
    const body = (method === "get" || method === "delete" ) ? "params" : "data";
    const config = {
        method,
        url,
        baseURL: API_URL,
        [body]: params || {},
        responseType: type,

    };
 
    config.headers = {
        "Content-Type": "application/json; charset=utf-8",
    };
    
    if (login) {
        // The tag records *intent* — "this call is only meaningful for a signed-in
        // user" — and must not depend on whether a token happens to be present.
        // Tying it to token presence would have exempted the exact scenario this
        // fix exists for: cookie cleared out of band, request goes out bare, 401
        // comes back, and nothing recognises it as an expiry.
        config[AUTH_REQUEST_FLAG] = !isAuthEndpoint(url);

        // The header, by contrast, stays conditional: no token means no header,
        // never the literal "Bearer undefined".
        const token = cookie.get('token');
        if (token) {
            config.headers.Authorization = "Bearer " + token;
        }
    }
    const company = localStorage.getItem('company') || 1
    if (company) {
      const params = config.params || {}
      params.company = company
      config.params = params
    }
  
    return axios.request(config);
};

/**
 * The single 401 choke point.
 *
 * Any authenticated request that comes back 401 means the session is over —
 * the cookie was cleared out of band (another tab logged out, a websocket
 * logout arrived, or the session cookie died with the browser) while React
 * state still believed we were signed in. One signal is raised here; the
 * cookie clearing, toast and redirect are AuthProvider's job.
 *
 * Guards, in order:
 *  - only fires for requests made on behalf of a signed-in user
 *    (AUTH_REQUEST_FLAG), so the login POST, forgot/reset-password, the public
 *    job pages, the token-based leave approval and the direct-to-S3 uploads can
 *    all 401/403 without logging anybody out. Every one of those either passes
 *    login=false or is an auth endpoint;
 *  - never fires for the token endpoints, where 401 means "wrong password";
 *    the Login form's own .catch keeps showing that inline;
 *  - never fires while sitting on /login, so an expiry cannot bounce the user
 *    back to a page they are already on;
 *  - notifyAuthExpired() latches, so N parallel 401s collapse to one redirect.
 *
 * Every error is re-thrown untouched, 401 included, so all existing .catch
 * handlers (uniformErrorMessage and friends) keep behaving exactly as before.
 */
const installAuthInterceptor = () => {
    // src/index.jsx calls module.hot.accept(), so in dev this module re-executes
    // on every edit. Without a guard each reload would stack another copy of the
    // interceptor onto the shared axios instance, and one 401 would run the
    // handler N times. Harmless in a production build; confusing in dev.
    if (axios.__essAuthInterceptorId !== undefined) {
        axios.interceptors.response.eject(axios.__essAuthInterceptorId);
    }

    axios.__essAuthInterceptorId = axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status;
            const wasAuthenticated = error?.config?.[AUTH_REQUEST_FLAG] === true;
            const onLoginPage = window.location.pathname === "/login";

            if (status === 401 && wasAuthenticated && !onLoginPage) {
                notifyAuthExpired("401");
            }

            return Promise.reject(error);
        }
    );
};

installAuthInterceptor();
